import { useTranslation } from 'react-i18next';
import { Box, Button, Collapse, Group, Text, ThemeIcon, UnstyledButton } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconCheck, IconChevronDown } from '@tabler/icons-react';
import { isStepNavigable } from '@/lib/dealSteps';
import type { BargainStep } from '@/types/deals';
import classes from './DealStepper.module.css';

interface DealStepperProps {
  readonly steps: readonly BargainStep[];
  /** id выбранного для просмотра шага (для подсветки); по умолчанию — текущий. */
  readonly selectedId?: number;
  /** Переход к шагу (только пройденные/текущий навигируемы). */
  readonly onSelect: (stepId: number) => void;
}

type StepState = 'done' | 'current' | 'future';

function stepState(step: BargainStep): StepState {
  if (step.isCurrent) {
    return 'current';
  }
  if (step.hasDone) {
    return 'done';
  }
  return 'future';
}

// Степпер сделки (read-only). Источник — stepsList с сервера: порядок по num, состояние по
// hasDone/isCurrent/isActiveUser/isActiveContragent. UI ничего не хардкодит.
export default function DealStepper({ steps, selectedId, onSelect }: DealStepperProps) {
  const { t } = useTranslation();
  const [opened, { toggle }] = useDisclosure(false);

  const ordered = [...steps].sort((a, b) => (a.num ?? 0) - (b.num ?? 0));
  const currentIndex = ordered.findIndex((s) => s.isCurrent);
  const current = currentIndex >= 0 ? ordered[currentIndex] : undefined;

  function currentHint(step: BargainStep): string {
    if (step.isActiveUser) {
      return t('app.deals.stepper.yourTurn');
    }
    if (step.isActiveContragent) {
      return t('app.deals.stepper.waitContragent');
    }
    return t('app.deals.stepper.processing');
  }

  const list = (
    <Box className={classes.list}>
      {ordered.map((step, index) => {
        const state = stepState(step);
        const isLast = index === ordered.length - 1;
        const navigable = isStepNavigable(step);
        const selected = selectedId != null && step.id === selectedId;
        const inner = (
          <>
            <Text
              className={`${classes.name}${
                state === 'current' ? ` ${classes.nameCurrent}` : state === 'future' ? ` ${classes.nameFuture}` : ''
              }`}
              fz="sm"
              aria-current={state === 'current' ? 'step' : undefined}
            >
              {step.name}
            </Text>
            {state === 'current' ? (
              <Text fz="xs" c={step.isActiveUser ? 'brand.7' : 'gray.7'} fw={500} mt={2}>
                {currentHint(step)}
              </Text>
            ) : null}
          </>
        );
        return (
          <Box key={step.id ?? step.num ?? index} className={classes.item}>
            <Box className={classes.rail}>
              {state === 'done' ? (
                <ThemeIcon size={28} radius="xl" color="lime" variant="filled" aria-hidden>
                  <IconCheck size={16} />
                </ThemeIcon>
              ) : state === 'current' ? (
                <ThemeIcon size={28} radius="xl" color="brand" variant="filled" aria-hidden>
                  <Text fz="sm" fw={700} c="white">
                    {step.num}
                  </Text>
                </ThemeIcon>
              ) : (
                <ThemeIcon size={28} radius="xl" color="gray" variant="light" aria-hidden>
                  <Text fz="sm" fw={600} c="gray.6">
                    {step.num}
                  </Text>
                </ThemeIcon>
              )}
              {!isLast ? <Box className={`${classes.line}${state === 'done' ? ` ${classes.lineDone}` : ''}`} /> : null}
            </Box>
            {navigable && step.id != null ? (
              <UnstyledButton
                className={`${classes.content} ${classes.clickable}${selected ? ` ${classes.selected}` : ''}`}
                onClick={() => onSelect(step.id!)}
                aria-label={t('app.deals.readonly.viewStep', { name: step.name })}
                aria-pressed={selected}
              >
                {inner}
              </UnstyledButton>
            ) : (
              <Box className={classes.content}>{inner}</Box>
            )}
          </Box>
        );
      })}
    </Box>
  );

  return (
    <>
      {/* Десктоп — полный список. */}
      <Box visibleFrom="sm">{list}</Box>

      {/* Мобайл — компактный индикатор «Шаг N из M» + раскрытие. */}
      <Box hiddenFrom="sm">
        <Group justify="space-between" wrap="nowrap" gap="sm">
          <Box style={{ minWidth: 0 }}>
            <Text fz="xs" c="gray.7">
              {t('app.deals.stepper.counter', { current: currentIndex >= 0 ? currentIndex + 1 : ordered.length, total: ordered.length })}
            </Text>
            <Text fz="sm" fw={600} truncate>
              {current?.name ?? ordered[ordered.length - 1]?.name}
            </Text>
          </Box>
          <Button
            variant="subtle"
            size="compact-sm"
            onClick={toggle}
            rightSection={
              <IconChevronDown size={16} style={{ transform: opened ? 'rotate(180deg)' : undefined, transition: 'transform 150ms' }} />
            }
          >
            {t('app.deals.stepper.allSteps')}
          </Button>
        </Group>
        <Collapse expanded={opened}>
          <Box mt="md">{list}</Box>
        </Collapse>
      </Box>
    </>
  );
}
